import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {cn} from '@features/ui/utils';
import {useState} from 'react';
import UserIcon from '@/icons/User.svg';
import type {teamRoleEnum} from '@features/db/schema';

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

type MinimalTeam = {
  name: string | null | undefined;
  avatarUrl: string | null | undefined;
  members: {
    createdAt: Date;
    role: (typeof teamRoleEnum.enumValues)[number];
    user: {
      name?: string | null;
      email: string;
    };
  }[];
};
export function getTeamDisplayName(team: MinimalTeam) {
  if (team.name) {
    return team.name;
  }

  const owner = team.members
    .toSorted((a, b) => +a.createdAt - +b.createdAt)
    .find((a) => a.role === 'owner');
  const ownerName = owner?.user.name ?? owner?.user.email ?? 'unknown';

  return `${capitalize(ownerName)}'s team`;
}

export function UserName({user: u}: {user: {email: string; name?: string}}) {
  if (u.name) {
    // The onFocusCapture / stopPropagation is sad, but prevents the tooltip
    // from showing on a modal opening
    // See: https://github.com/radix-ui/primitives/issues/2248

    return (
      <span onFocusCapture={(ev) => ev.stopPropagation()}>
        <Tooltip>
          <TooltipTrigger>{u.name}</TooltipTrigger>
          <TooltipContent>{u.email}</TooltipContent>
        </Tooltip>
      </span>
    );
  }

  return u.email;
}

export function OptionalUserAvatar({
  image,
  className,
}: {
  image?: string;
  className?: string;
}) {
  const [isBroken, setIsBroken] = useState(false);

  if (!image || isBroken) {
    return null;
  }

  return (
    <img src={image} className={className} onError={() => setIsBroken(true)} />
  );
}

export function UserAvatar({
  image,
  className,
  fallback,
}: {
  image?: string;
  className?: string;
  fallback?: React.ReactNode;
}) {
  const [isBroken, setIsBroken] = useState(false);

  if (!image || isBroken) {
    if (fallback !== undefined) {
      return fallback;
    }

    return (
      <div
        className={cn(
          'flex items-center justify-center overflow-hidden rounded-full bg-[#F1F1F1] text-gray-400',
          className,
        )}
      >
        <UserIcon />
      </div>
    );
  }

  return (
    <img src={image} className={className} onError={() => setIsBroken(true)} />
  );
}

export function TeamAvatar({
  team,
  className,
  variant,
}: {
  team: MinimalTeam;
  className: string;
  variant?: 'ring' | 'no-ring';
}) {
  if (!team.avatarUrl) {
    const firstLetter = getTeamDisplayName(team)[0].toLocaleUpperCase();
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-md bg-gray-200 text-[10px] font-black text-gray-400',
          className,
        )}
      >
        {firstLetter}
      </div>
    );
  }

  return (
    <div className='relative size-5'>
      <img
        src={team.avatarUrl}
        alt=''
        className={cn('rounded-md object-cover', className)}
      />
      {variant !== 'no-ring' && (
        <div className='pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-black/10' />
      )}
    </div>
  );
}
